import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { getGetShearConnectionUsageQueryKey } from '@api/project-shear-connections/project-shear-connections'
import { getGetShearAnchorChecksQueryKey } from '@api/shear-anchor-checks/shear-anchor-checks'
import { usePutShearAnchorConnectorSelections } from '@api/shear-anchor-connector-selections/shear-anchor-connector-selections'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { buildErrorMessage } from 'src/constants/errors'
import { ConnectorSelection } from '../../connectorSelection'
import { buildShearAnchorSelectionEdits } from './utils'

/**
 * Fixes the Schubverbinder of Schubanker locations (or hands them back to the prioritization).
 * The checks and the Schubverbinder usage are refetched afterwards; the backend recomputes the
 * edited locations on that read.
 */
const useSaveShearAnchorSelections = () => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const { mutate, isLoading: isSaving } = usePutShearAnchorConnectorSelections({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(getGetShearAnchorChecksQueryKey(projectId)),
          queryClient.invalidateQueries(getGetShearConnectionUsageQueryKey(projectId)),
        ])
      },
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Speichern der Schubanker'),
          { variant: 'error' },
        ),
    },
  })

  const saveShearAnchorSelections = useCallback(
    (
      locations: ShearAnchorLocationSelection[],
      selection: ConnectorSelection,
      onSuccess?: () => void,
    ) =>
      mutate(
        {
          projectGuid: projectId as string,
          data: buildShearAnchorSelectionEdits(locations, selection),
        },
        { onSuccess },
      ),
    [mutate, projectId],
  )

  return { saveShearAnchorSelections, isSaving }
}

export default useSaveShearAnchorSelections
